/**
 * Server-side feature flags that decide whether a nav item exists at all.
 *
 * A kill switch makes a feature ABSENT, so a flagged nav item is hidden until
 * the server has said it is on. Anything else (still loading, request failed,
 * unknown flag) stays hidden: fail CLOSED. The answer is fetched once per
 * session and shared by the Sidebar and the command palette; a failed fetch is
 * not cached, so the next mount retries.
 */
import { useEffect, useState } from 'react';

export type NavFlags = Record<string, boolean>;

let inflight: Promise<NavFlags> | null = null;

export const loadNavFlags = (): Promise<NavFlags> => {
  if (!inflight) {
    // Imported lazily: the API client pulls in the auth chain, which the
    // palette / rail tests (and any import of this leaf) should not need.
    inflight = import('../../services/bringCasesApi')
      .then((m) => m.bringCasesApi.getStatus())
      .then((s) => ({
        bring_cases: s.enabled === true,
        // Import from a cause-list PDF / diary photo: its own switch, inside Bring your cases.
        bring_cases_extract: s.enabled === true && s.extract_enabled === true,
      }))
      .catch((): NavFlags => {
        inflight = null; // do not cache a failure
        return { bring_cases: false, bring_cases_extract: false };
      });
  }
  return inflight;
};

/** Test seam: forget the cached answer. */
export const resetNavFlagsCache = () => { inflight = null; };

export function useNavFlags(): NavFlags {
  const [flags, setFlags] = useState<NavFlags>({});
  useEffect(() => {
    let live = true;
    loadNavFlags().then((f) => { if (live) setFlags(f); });
    return () => { live = false; };
  }, []);
  return flags;
}
